const defaultNS = '' as const;

const resources = {
  en: {
    users: {
      loading: 'Loading users...',
      title: 'Users',
      refetching: 'Refetching users...',
      refetch_success: 'Users updated!',
      refetch_error: 'Failed to update users.',
      refetch_tooltip: 'Click here to refetch users.',
      super_admin_column_header: 'Super Admin',
      grant_super_admin_title: 'Grant Super Admin?',
      grant_super_admin_description:
        'Grant {{name}} Super Admin access across the entire portal? They will have full platform-wide administrative privileges.',
      revoke_super_admin_title: 'Revoke Super Admin?',
      revoke_super_admin_description:
        'Revoke Super Admin access from {{name}}? They will lose platform-wide administrative privileges across the portal.',
      grant_super_admin_loading: 'Granting Super Admin to {{name}}...',
      grant_super_admin_success: '{{name}} has been granted Super Admin access.',
      grant_super_admin_error: 'Failed to grant Super Admin to {{name}}.',
      revoke_super_admin_loading: 'Revoking Super Admin from {{name}}...',
      revoke_super_admin_success: 'Super Admin access revoked from {{name}}.',
      revoke_super_admin_error: 'Failed to revoke Super Admin from {{name}}.',
      super_admin_self_tooltip: 'You cannot change your own Super Admin status.',
      grant_super_admin_tooltip: 'Grant Super Admin access',
      revoke_super_admin_tooltip: 'Revoke Super Admin access',
      global_table_title: 'All Users (Super Admin View)',
      global_table_subtitle: 'Viewing all users across the portal. Use filters to narrow results.',
      is_super_admin_column_header: 'Platform Super Admin',
      is_super_admin_yes: 'Super Admin',
      is_super_admin_no: 'Standard',
      updated_at_column_header: 'Updated At',
      last_login_at_column_header: 'Last Login',
      can_create_studies_column_header: 'Create Studies',
      can_create_runs_column_header: 'Create Runs',
      organization_column_header: 'Organization',
    },
    auth: {
      saml: {
        provider_name_required: 'Provider name is required',
        email_domain_required: 'Email domain is required',
        attr_email_required: 'Attribute email is required',
        attr_first_name_required: 'Attribute first name is required',
        attr_last_name_required: 'Attribute last name is required',
        attr_roles_required: 'Attribute roles is required',
        metadata_required: 'Either Metadata URL or Metadata File is required',
        portal_parameters: 'Portal SAML Parameters',
        portal_parameters_tooltip: 'Portal SAML Parameters further information.',
        recipient: 'Recipient',
        audience: 'Audience',
        configuring: 'Configuring...',
        configure_provider: 'Configure SAML Provider',
        updating_config: 'Updating authentication configuration...',
        config_success: 'SAML provider configured successfully!',
        config_error: 'Failed to configure SAML provider',
        loading_settings: 'Loading Authentication Settings...',
        error_loading_settings: 'Error loading authentication settings.',
        saml_tab: 'SAML',
        identity_provider: 'SAML Identity Provider',
        identity_provider_tooltip: 'SAML Identity Provider further information.',
        provider_name_label: 'Provider Name',
        email_domain_label: 'Email Domain',
        metadata_url_label: 'Metadata URL',
        or: 'or',
        selected_metadata_file: 'Selected: {{file}}',
        upload_metadata_xml: 'Upload Metadata XML',
        user_attribute_mapping: 'User Attribute Mapping',
        user_attribute_mapping_tooltip: 'User Attribute Mapping further information.',
        attr_email_label: 'Attribute Email',
        attr_first_name_label: 'Attribute First Name',
        attr_last_name_label: 'Attribute Last Name',
        attr_roles_label: 'Attribute Roles',
        aria_tablist_auth_settings: 'Authentication settings',
        aria_portal_parameters: 'Portal SAML Parameters',
        aria_identity_provider: 'SAML Identity Provider',
        aria_user_attribute_mapping: 'User Attribute Mapping',
        refresh_metadata: 'Refresh Metadata',
        refresh_provider: 'Refresh Provider',
        refreshing_metadata: 'Refreshing...',
        refreshing_provider: 'Refreshing...',
        clear_file: 'Clear',
        please_fix_errors: 'Please fix the following errors:',
        options_tooltip: 'Click here to reveal data refresh options.',
      },
    },
    create: {
      errors: {
        noStudiesAvailable: 'No studies available. Please create a study first before creating an instrument run.',
      },
      run: {
        studyId: 'Study ID',
        orgId: 'Org ID',
        addDescription: 'Add Description',
        clickToCreateStudy: 'Click here to create a new study.',
        created: 'has been created successfully!',
        submitted: 'has been submitted successfully!',
        creating: 'Creating Run...',
        failed: 'Failed to create instrument run. Please try again.',
        noOrgSelected: 'No organization selected',
        noStudySelected: 'Please select a study',
        demuxIndex: 'Demux',
        orgName: 'Organization Name',
        sampleNum: 'Sample #',
        studyName: 'Study Name',
        wellPosition: 'Well Pos',
        saveAsDraft: 'Save',
        savedAsDraft: 'has been saved as a draft!',
        updated: 'has been updated successfully!',
        failedToSaveDraft: 'Failed to save draft. Please try again.',
        validation: {
          nameRequired: 'Name is required',
          nameInvalidChars: 'Name cannot contain apostrophes or backticks',
          numberOfSamplesRequired: 'Number of samples is required',
          sampleNameRequired: 'Sample name is required',
          studyNameRequired: 'Study name is required',
          orgRequired: 'Organization is required',
          wellPositionRequired: 'Well position is required',
          uploadedSamplesMismatch: 'Number of uploaded samples must match the expected number of samples',
          allSampleFieldsRequired: 'All sample fields must be filled with valid values',
          sampleNameConflict: 'One or more sample names already exist in their respective studies',
        },
      },
      experiment: {
        fields: {
          assayType: {
            label: 'Assay Type',
            options: {
              alpha: 'Alpha Synuclein',
              broadscale: 'Broadscale',
              tau: 'Tau',
            },
          },
          assayVersion: {
            label: 'Assay Version',
            options: {
              naut: 'Tau',
              broadscale: 'Broadscale',
            },
          },
          description: 'Instrument Run Description',
          lotNumber: 'Prep Kit Lot Number',
          name: 'Instrument Run Name',
          noOfSamples: {
            label: 'Number of Samples',
            options: {
              four: '4',
              twelve: '12',
              twentyfour: '24',
            },
          },
          species: {
            label: 'Species',
            options: {
              human: 'Human',
              mouse: 'Mouse',
            },
          },
          study: {
            label: 'Study',
            options: {
              lecanemab: 'Lecanemab',
              lungs: 'Lungs',
              mj: 'MJ Fox Study',
            },
          },
        },
      },
      study: {
        create: 'Create Study',
        creating: 'Creating Study...',
        failed: 'Failed to create study. Please try again.',
        success: 'study has been created successfully!',
      },
    },
    errors: {
      copySuccess: 'Error copied to clipboard.',
      copyTooltip: 'Click here to copy the error message.',
      levels: {
        err: 'Error',
        warn: 'Warning',
      },
    },
    experiments: {
      name: 'Experiments',
      new: 'Create New Experiment',
      cols: {
        code: 'Code',
        dateComplete: 'Completed On',
        dateSubmit: 'Submitted On',
        name: 'Experiment Name',
        instrument: 'Instrument',
        noOfSamples: 'Samples',
        status: 'Status',
        study: 'Study',
        submitBy: 'Submitted By',
        type: 'Type',
      },
    },
    general: {
      actions: 'Actions',
      add_user: 'Add User',
      adding: 'Adding...',
      cancel: 'Cancel',
      code: 'Code',
      copy_question: 'Copy?',
      confirm: 'Confirm',
      copied: 'Copied: ',
      failedCopy: 'Failed to copy',
      begin_typing_here: 'Begin typing here...',
      created: 'Created',
      search_user: 'Add User',
      created_at: 'Created at',
      created_by: 'Created by',
      current_users: 'Current Users',
      desc: 'Description',
      email: 'Email',
      empty: '-',
      failed: 'Failed',
      no: 'No',
      is_admin: 'Make Admin',
      name: 'Name',
      logging_in: 'Logging in...',
      remove: 'Remove',
      removed: 'removed successfully',
      removing: 'Removing...',
      roles: 'Roles',
      save: 'Save',
      selected: 'Selected',
      settings: 'Settings',
      success: 'Success',
      date_time_format: 'Date & time format',
      applies_across_app: 'Applies across the app',
      date_format_description: 'Choose how dates and times appear in tables, activity feeds, and dashboards.',
      date_format_options: 'Date format options',
      choose_date_time_format: 'Choose date and time format',
      date_format_updated: 'Date format updated to {{format}}',
      date_format_presets: {
        short: {
          label: 'Short',
          description: 'Compact numeric month/day/year with time',
        },
        medium: {
          label: 'Medium',
          description: 'Readable month abbreviation with day, year, and time',
        },
        long: {
          label: 'Long',
          description: 'Detailed weekday, month, day, year, and time',
        },
      },
      updated: 'Modified',
      updated_at: 'Modified at',
      updated_by: 'Modified by',
      user_id: 'User ID',
      yes: 'Yes',
      reset: 'Reset',
      sort: {
        asc: 'Sort ascending',
        clear: 'Clear sort',
        desc: 'Sort descending',
      },
      submit: 'Submit',
      submitting: 'Submitting',
      view: {
        card: 'card',
        card_v: 'View Cards',
        table: 'table',
        table_v: 'View Table',
      },
      user_management: {
        owner: 'Owner',
        allowed_users: 'Allowed Users',
        promote_title: 'Promote to Owner?',
        promote_description:
          'This will give {{name}} full control over this study, including the ability to manage other users.',
        promote_tooltip: 'Promote user to owner',
        revoke_title: 'Revoke Access?',
        revoke_description: '{{name}} will no longer be able to access this study.',
        revoke_owner_note: 'Note: This will demote them from owner status.',
        revoke_tooltip: "Revoke user's access",
        select_user_error: 'Please select a user',
        adding_user: 'Adding {{name}}...',
        adding: 'Adding user...',
        added_success: '{{name}} was granted access successfully',
        added_success_generic: 'User access granted successfully',
        added_error: 'Failed to grant access to {{name}}: {{error}}',
        added_error_generic: 'Failed to grant access: {{error}}',
        revoking_user: 'Revoking access for {{name}}...',
        revoking: 'Revoking access...',
        revoked_success: "{{name}}'s access was revoked successfully",
        revoked_success_generic: 'User access revoked successfully',
        revoked_error: 'Failed to revoke access for {{name}}: {{error}}',
        revoked_error_generic: 'Failed to revoke access: {{error}}',
        promoting_user: 'Promoting {{name}} to owner...',
        promoting: 'Promoting user...',
        promoted_success: '{{name}} was promoted to owner successfully',
        promoted_success_generic: 'User promoted to owner successfully',
        promoted_error: 'Failed to promote access to {{name}}: {{error}}',
        promoted_error_generic: 'Failed to promote user: {{error}}',
        adding_user_row: '{{name}} (Adding...)',
        adding_role: '...',
      },
      unauthorized: {
        title: 'Unauthorized',
        message1: 'You do not have permission to access this page.',
        message2: 'Please contact your administrator if you believe this is an error.',
        go_home: 'Go to Studies',
        attempted_path: 'You tried to access: {{path}}',
      },
      alias: 'Alias',
      primary_contact: 'Primary Contact',
      it_contact: 'IT Contact',
      copy_contact_details: 'Copy all contact details',
      copy_contact_success: 'Contact details copied!',
      copy_contact_error: 'Failed to copy contact details',
    },
    instruments: {
      title: 'Instruments',
      fetching: 'Fetching instruments...',
      refetching: 'Refreshing instruments...',
      refetch_success: 'Instruments updated!',
      refetch_error: 'Failed to refresh instruments.',
      refetch_tooltip: 'Refresh instruments',
      create_instrument: 'Add Instrument',
      create_title: 'Add Instrument',
      edit_title: 'Edit Instrument',
      save: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel',
      save_error: 'Failed to save instrument. Please try again.',
      create_saving: 'Adding instrument...',
      create_success: 'Instrument "{{name}}" added successfully.',
      create_error: 'Failed to add instrument. Please try again.',
      update_saving: 'Updating instrument...',
      update_success: 'Instrument "{{name}}" updated successfully.',
      update_error: 'Failed to update instrument. Please try again.',
      create_error_detail: 'Failed to add instrument: {{error}}',
      update_error_detail: 'Failed to update instrument: {{error}}',
      delete_confirm: 'Are you sure you want to delete instrument "{{name}}"?',
      delete_confirm_short: 'Delete?',
      delete_dialog_title: 'Delete "{{name}}"?',
      delete_dialog_description: 'Are you sure you want to permanently delete "{{name}}"?',
      delete_dialog_sub_description: 'This action cannot be undone.',
      delete_deleting: 'Deleting "{{name}}"...',
      delete_yes: 'Yes, Delete',
      delete_success: 'Instrument "{{name}}" deleted successfully.',
      delete_error: 'Failed to delete "{{name}}": {{error}}',
      edit_tooltip: 'Edit instrument',
      delete_tooltip: 'Delete instrument',
      field_name: 'Name',
      field_name_placeholder: 'e.g. Nemo A',
      field_serial_num: 'Serial Number',
      field_serial_num_placeholder: 'e.g. SN-00123',
      field_model: 'Model',
      field_model_placeholder: 'e.g. Nemo',
      field_organization: 'Organization',
      field_is_available: 'Available',
      field_next_pm_at: 'Next Preventative Maintenance',
      validation_name_required: 'Instrument name is required.',
      validation_org_required: 'Please select an organization',
      col_serial_num: 'Serial Number',
      col_model: 'Model',
      col_is_available: 'Available',
      col_next_pm_at: 'Next PM',
      available: 'Available',
      unavailable: 'Unavailable',
      empty_state: 'No instruments found. Create one to get started.',
    },
    login: {
      graphic: {
        heading: 'Welcome to the Proteome Analysis Cloud Portal',
        subtitle:
          'Accelerate your proteomics research with scalable, high-throughput protein identification and quantification.',
        image_alt: 'Nautilus Proteome Analysis Platform',
      },
      buttons: {
        login: 'Login',
        loginsso: 'Login with SSO',
        logout: 'Logout',
        use_password: 'I have a password...',
        resend_code: 'Resend Code',
      },
      logging_out: 'Logging out...',
      form: {
        heading: 'Login to the Portal',
        description: 'Sign in to access your Nautilus account.',
        errors: {
          email: {
            required: 'An email is required.',
            short: 'An email must be at least {{min}} characters long.',
          },
          password: {
            required: 'A password is required.',
            short: 'Your password must be at least {{min}} characters long.',
          },
          reset_password_failed: 'Failed to reset password. Please try again.',
          reset_password_link_invalid:
            'This link is no longer valid. You will be automatically redirected to the login page.',
        },
        labels: {
          email: 'E-Mail',
          email_required: 'Email is required.',
          invalid_email: 'Invalid email address.',
          pass: 'Password',
          pass_required: 'A password is required.',
          passTooltip: 'Click to toggle text visibility.',
          valid_email_required: 'You must enter a valid email address.',
          reset: 'Reset',
          submit: 'Submit',
          validating: 'Validating',
          forgot_password: 'Forgot password?',
          forgot_password_instructions:
            'Enter your email address and we will send you instructions to reset your password.',
          send_reset_link: 'Send Reset Link',
          invalid_reset_link: 'Invalid Reset Link',
          invalid_reset_link_message_1: 'This password reset link is invalid or incomplete.',
          invalid_reset_link_message_2: 'This can happen if the link was copied incorrectly or has expired.',
          request_new_reset_link: 'Request New Reset Link',
          return_to_login: 'Return to Login',
          forgot_password_success: 'If your email is registered, a reset link was sent to {{email}}.',
          forgot_password_error: 'Failed to send reset link. Please try again.',
          reset_password: 'Reset Password',
          reset_password_instructions: 'Enter the reset token you received and your new password.',
          reset_password_success: 'Your password has been reset successfully.',
          reset_password_error: 'Failed to reset password. Please try again.',
          token: 'Reset Token',
          new_password: 'New Password',
          sending: 'Sending...',
          resetting_password: 'Resetting password...',
        },
      },
      loading: {
        default: 'Fetching latest data',
      },
      auto_redirect: 'Redirecting in {{count}}s...',
    },
    logo: { alt: 'Nautilus Biotechnology Portal' },
    noOrg: {
      title: 'Welcome to Nautilus Biotechnology Portal.',
      message: 'Please contact your admin to add you to an organization.',
    },
    orgs: {
      fetching: 'Fetching Organizations',
      id: 'ID',
      name: 'Organizations',
      current_organization: 'Selected Organization: {{name}}',
      manage_users_title: 'Manage Users - {{name}}',
      manage_users_tooltip: 'Click here to manage users in this organization.',
      user_added_success: 'User added successfully to {{orgName}}',
      user_added_failed: 'Failed to add user to {{orgName}}',
      user_id_description: 'Enter the UUID of the user to add to this organization',
      user_not_found: 'User not found with that ID',
      org_not_found: 'Organization not found',
      user_permissions: 'User Permissions',
      search_user_placeholder: 'Type valid email here...',
      search_user_description: "Add a valid email above and select the role(s) you'd like applied to this user.",
      please_select_user: 'Please select a user from the search results',
      no_users_in_org: 'No users have been added to this organization yet.',
      confirm_remove_user: 'Are you sure you want to remove {{name}} from this organization?',
      user_removed_success: '{{name}} removed successfully from {{orgName}}',
      user_removed_failed: 'Failed to remove user from {{orgName}}',
      user_search_failed: 'Failed to search users. Please try again.',
      user_search_invalid_response: 'Received invalid data from user search. Please try again.',
      org_switched_success: 'Switched to {{name}}',
      org_switched_save_error: 'Switched to {{name}}, but the selection could not be saved.',
      select_organization: 'Select {{name}} organization',
      refetching: 'Refetching organizations...',
      refetch_success: 'Organizations updated!',
      refetch_error: 'Error loading organization: {{message}}',
      list_refetch_error: 'Failed to update organizations.',
      refetch_tooltip: 'Click here to refetch organizations.',
      email_invalid: 'Please enter a valid email.',
      select_at_least_one_role: 'Select at least one role.',
      updating_roles: 'Updating roles...',
      roles_updated: 'Roles updated for {{userName}}',
      removing_roles: 'Removing roles...',
      roles_removed: 'Roles removed for {{userName}}',
      failed_update_roles: 'Failed to update roles for {{userName}}',
      edit_roles: 'Edit roles',
      roles_selector: 'roles selector',
      save_roles: 'Save roles.',
      cancel_role_editing: 'Cancel role editing.',
      org_selector_selected: 'Selected Organization',
      org_selector_switch: 'Switch Organization:',
      org_selector_tooltip_switch: 'Click here to switch to {{name}}',
      org_loading: 'Loading Organizations...',
      org_selector_switch_to: 'Switch to {{name}}',
      org_selector_your_role: 'Your Role',
      org_selector_id_label: 'ID:',
      org_selector_copy_tooltip: 'Click here to copy the currently selected organization ID.',
      org_selector_role_switch_success: 'Switched to {{role}}',
      org_selector_role_switch_failed: 'Failed to switch role',
      org_selector_org_id_copied: 'Organization ID copied to clipboard',
      org_selector_org_id_copy_failed: 'Failed to copy organization ID',
      org_selector_tooltip_open: 'Open Organization selector.',
      org_selector_no_roles: '⚠️ No roles available for this organization',
      org_selector_search_placeholder: 'Search organizations...',
      org_selector_no_results: 'No organizations found.',
      create_org: 'Create Organization',
      create_org_title: 'Create New Organization',
      create_org_name_label: 'Organization Name',
      create_org_name_placeholder: 'Enter organization name',
      create_org_creating: 'Creating {{name}}.',
      create_org_submit: 'Create Organization',
      create_org_success: 'Organization created successfully',
      create_org_error: 'Failed to create organization',
      create_org_cancel: 'Cancel',
      create_org_section_basic: 'Basic Information',
      create_org_section_primary_contact: 'Primary Contact',
      create_org_section_it_contact: 'IT Contact',
      create_org_section_domain: 'Domain',
      create_org_section_subscription: 'Subscription',
      create_org_address_label: 'Address',
      create_org_address_placeholder: 'Street address, City, State, ZIP',
      create_org_alias_label: 'Alias',
      create_org_alias_placeholder: 'e.g. ACME',
      create_org_first_name_label: 'First Name',
      create_org_first_name_placeholder: 'Enter first name',
      create_org_last_name_label: 'Last Name',
      create_org_last_name_placeholder: 'Enter last name',
      create_org_phone_label: 'Phone',
      create_org_phone_placeholder: '+1 555 000 0000',
      create_org_email_label: 'Email',
      create_org_email_placeholder: 'name@example.com',
      create_org_domain_label: 'Domain',
      create_org_domain_placeholder: 'example.com',
      create_org_subscription_active_label: 'Active',
      create_org_subscription_expiration_label: 'Expiration Date',
      edit_org_save_all: 'Save All',
      validating: 'Validating',
      validation_name_required: 'Organization name is required.',
      validation_name_min_length: 'Organization name must be at least 3 characters long.',
      validation_address_required: 'Address is required.',
      validation_alias_required: 'Alias is required.',
      validation_alias_min_length: 'Alias must be at least 2 characters long.',
      validation_first_name_required: 'First name is required.',
      validation_last_name_required: 'Last name is required.',
      validation_phone_invalid: 'Enter a valid phone number.',
      validation_email_invalid: 'Enter a valid email address.',
      validation_domain_required: 'Domain is required.',
      validation_domain_invalid: 'Enter a valid domain.',
      validation_expiration_required: 'Expiration date is required when subscription is active.',
      create_org_error_detail: 'Failed to create organization: {{message}}',
      edit_org_saving: 'Saving organization...',
      edit_org_success: 'Organization saved successfully',
      edit_org_error: 'Failed to save organization',
      edit_org_error_detail: 'Failed to save organization: {{message}}',
    },
    runs: {
      create_new_run: 'Create New Run',
      create_study: 'Create Study',
      header: {
        name: 'Name',
        description: 'Description',
        edit: 'Edit',
        type: 'Type',
        species: 'Species',
        instrument_id: 'Instrument ID',
        id_label: 'ID: {{id}}',
        description_label: 'Description:',
        description_aria: 'Edit instrument run description',
        tooltip_status_info: 'Click here to reveal status update information.',
        created: 'Created',
        updated: 'Updated',
        submitted: 'Submitted',
        completed: 'Completed',
        samples: 'Samples',
        assay_version: 'Assay Version',
        consumables: 'Consumables',
        runs_label: 'Runs',
        share: 'Share',
        share_coming_soon: 'Share — coming soon',
        pin_to_start_run: 'Pin to Start Run',
        instrument: 'Instrument',
        modified: 'Modified',
        started: 'Started',
        finished: 'Finished',
        submitted_by: 'Submitted By',
        instrument_status: 'Instrument Status',
        decode_status: 'Decode Status',
        sample_qc: 'Sample QC',
        controls: 'Controls',
        col_flowcell: 'Flowcell',
        col_lane: 'Lane',
        col_well: 'Well',
        col_demux_id: 'Demux ID',
        col_sample: 'Sample',
        col_library_group: 'Library Group',
        col_sample_quality_qc: 'Sample Quality QC',
        col_decode_qc: 'Decode QC',
        col_lane_qc: 'Lane QC',
        col_controls_qc: 'Controls QC',
        col_warning: 'Warning',
        col_sample_group: 'Sample Group',
        col_study: 'Study',
        col_organization: 'Organization',
        cons_flowcell_1: 'Flowcell 1 SN',
        cons_flowcell_2: 'Flowcell 2 SN',
        cons_flowcell_3: 'Flowcell 3 SN',
        cons_sample_plate: 'Sample Plate SN',
        cons_detection_plate: 'Detection Plate SN',
        cons_system_reagents: 'System Reagents SN',
        cons_run_buffer: 'Run Buffer SN',
        cons_system_clean: 'System Clean SN',
        cons_additive_a: 'Run Buffer Additive A Lot ID',
        cons_additive_b: 'Run Buffer Additive B Lot ID',
        cons_additive_c: 'Run Buffer Additive C Lot ID',
        cons_lib_prep_a: 'Library Prep Kit A SN',
        cons_lib_prep_b: 'Library Prep Kit B SN',
        cons_lib_prep_c: 'Library Prep Kit C SN',
        cons_lib_prep_controls: 'Library Prep Kit Controls SN',
      },
      fetching: 'Fetching Instrument Runs data',
      new_run: 'New Run',
      edit_run: 'Edit Run',
      controls: {
        title: 'Controls',
        flowcell: 'Flow Cell',
        lane: 'Lane',
        well: 'Well',
        demux_id: 'Demux ID',
        sample_name: 'Sample Name',
        type: 'Type',
      },
      no_runs: 'No runs found',
      no_runs_p: 'Click the "Create New Run" button to get started.',
      no_studies_message: 'You need to create a study first before creating instrument runs.',
      run: {
        back: 'Back to Instrument Runs',
        fetching: 'Fetching Run data',
      },
      loading: 'Loading',
    },
    sampleManager: {
      download_template: 'Download Template',
      download_tooltip: 'Click here to download a csv file in the expected format.',
      upload_csv: 'Upload <1>CSV</1>',
      remove_all_samples: 'Remove All Samples',
      processing_csv: 'Processing CSV file...',
      no_valid_file: 'No valid file found',
      upload_success: 'Successfully uploaded {{count}} sample{{plural}}',
      invalid_response: 'Invalid response from server. Please try again.',
      empty_csv: 'CSV file is empty or contains no valid samples',
      upload_failed: 'Failed to upload CSV file',
      file_too_large: 'File size exceeds maximum allowed size of {{max}}MB',
      invalid_file_type: 'Invalid file type. Please upload a CSV file',
      csv_upload_error: 'CSV upload error:',
      study_select_placeholder: 'Search for a study...',
      study_select_dialog_title: 'Select a Study',
      study_select_empty_hint: 'Click to select a study',
      org_select_empty_hint: 'Click to select an organization',
      table_description: 'Upload a CSV file or double click a cell to edit it.',
      flow_cell: 'Flow Cell',
      lane: 'Lane',
      library_group: 'Library Group',
      sample_group: 'Sample Group',
      study_apply_to_all_empty: 'Apply to all empty rows',
      study_copy_to_rows: 'Apply to rows',
    },
    sidebar: {
      aut: 'Authentication',
      exp: 'Experiments',
      fle: 'Fleet',
      instr: 'Instruments',
      org: 'Organizations',
      runs: 'Runs',
      sam: 'Samples',
      stu: 'Studies',
      use: 'Users',
      toggle: {
        collapse: 'Collapse',
        expand: 'Expand',
      },
    },
    studies: {
      card_view: 'Studies card view',
      fetching: 'Fetching Study data',
      goToInstrumentRun: 'Go to Instrument Run',
      goToSample: 'Go to Sample',
      goToStudy: 'Go to Study',
      last_updated: 'Last updated: {{date}}',
      loading: 'Loading latest studies data',
      no_studies: 'No studies found',
      no_studies_p: 'Click the "Create New Study" button to get started.',
      refetch_error: 'Failed to refresh studies data.',
      refetch_success: 'Studies data successfully refreshed!',
      refetch_tooltip: 'Refresh Data',
      refetching: 'Refetching studies...',
      title: 'Samples',
      study: {
        back: 'Back to Studies',
        home: 'Studies',
        manage_users: 'Manage Users',
        edit: 'Edit',
        edit_study: 'Edit Study',
        edit_study_title: 'Edit Study',
        edit_success: '{{name}} updated successfully.',
        edit_failed: 'Failed to update study. Please try again.',
        edit_saving: 'Saving study...',
        share: 'Share',
        share_coming_soon: 'Share — coming soon',
        download: 'Download Study',
        download_coming_soon: 'Download — coming soon',
        edit_name_aria: 'Edit Study Name',
        edit_description_aria: 'Edit Study Description',
        no_org_selected: 'No organization selected',
        sample_groups: 'Sample Groups',
        samples_label: 'Total Samples',
        instrument_runs_label: 'Runs',
        broadscale_samples: 'Broadscale Samples',
        tau_samples: 'Tau Samples',
        studySamplesTitle: 'Study Samples',
        studySamplesTotal: 'Total',
        studySamplesPassedQc: 'Passed QC',
        assayDetailLink: 'Details',
        broadscaleDetailTitle: 'Broadscale v1 Samples',
        tauDetailTitle: 'Tau v1 Samples',
        proteinsQuantified: 'Proteins Quantified',
        validation: {
          name_required: 'Name is required.',
          name_min_length: 'Name must be at least 3 characters long.',
          name_no_apostrophe: "Name cannot contain apostrophes (').",
        },
      },
      table_view: 'Studies table view',
      page_title: 'Studies',
      study_count_one: '{{count}} Study',
      study_count_other: '{{count}} Studies',
      download_list: 'Download List',
      download_list_coming_soon: 'Download — coming soon',
      columnHeaders: {
        sampleNum: 'Sample #',
        sampleName: 'Sample Name',
        study: 'Study',
        contact: 'Contact',
        description: 'Description',
        numSamples: 'Samples',
        organization: 'Organization',
        instrumentRun: 'Run',
        wellPosition: 'Well Position',
        demuxIndex: 'Demux',
        proteoform: 'Proteoform',
        proteoformCount: 'Proteoform Count',
        abundance: 'Abundance',
        createdAt: 'Created At',
        sampleGroup: 'Sample Group',
        status: 'Status',
        runQc: 'Run QC',
        imageQc: 'Image QC',
        decodeQc: 'Decode QC',
        studyName: 'Study Name',
        runName: 'Run Name',
        assay: 'Assay',
        species: 'Species',
        created: 'Created',
        updated: 'Updated',
        createdBy: 'Created By',
        updatedBy: 'Updated By',
        proteinsDetected: 'Proteins Detected',
        proteoformGroup: 'Proteoform Group',
        quantityCpm: 'Quantity (cpm)',
        protein: 'Protein',
      },
      proteoformCounts: {
        title: 'Sample Proteoform Counts',
        noData: 'No proteoform count data available',
      },
      proteoforms: {
        loading: 'Loading proteoforms for this sample...',
        title: 'Sample Proteoforms',
        noData: 'No proteoform data available',
        error: 'Failed to load proteoforms for this sample.',
        name: 'Proteoform Name',
        abundance: 'Abundance',
        wellPosition: 'Well Position',
        createdAt: 'Created At',
        fetchingSampleData: 'Fetching sample data',
        tableAriaLabel: 'Proteoforms for sample',
      },
      samples: {
        collapseDetail: 'Collapse details for {{name}}',
        expandDetail: 'Expand details for {{name}}',
        details: {
          backToSamples: 'Back to Samples',
          detailsHeading: 'Details',
          additionalDataHeading: 'Additional Data',
          fieldOrganisation: 'Organisation',
          fieldStudy: 'Study',
          fieldInstrumentRun: 'Instrument Run',
          fieldWellPosition: 'Well Position',
          fieldDemuxIndex: 'Demux Index',
          fieldCreatedAt: 'Created At',
          fieldUpdatedAt: 'Updated At',
          notFound: 'Sample not found. Please navigate here from the samples list.',
          additionalDataPlaceholder: 'Coming soon.',
        },
      },
    },
  },
};

const ns = Object.keys(resources.en);

export { defaultNS, ns, resources };
